// RunTrack — where a run is in its lifecycle (FD §17.1), drawn as a row of
// stage segments: Queued → Provisioning → Running → Verifying → Done.
//
// "Waiting for you" (awaiting_approval) is not its own stage: the run is
// paused inside Running, so that segment turns signal-colored. A run that
// ended badly marks the segment it stopped at in red; since the status alone
// doesn't say which stage that was, a run that never started is treated as
// stopping at Provisioning and one that did start as stopping at Running.

import type { RunStatus } from '@goblic/shared'

const STAGES = [
  'Queued',
  'Provisioning',
  'Running',
  'Verifying',
  'Done',
] as const

type SegmentState =
  'done' | 'current' | 'waiting' | 'paused' | 'failed' | 'stopped' | 'todo'

interface TrackState {
  segments: SegmentState[]
  /** Index of the segment the run is at (or stopped at). */
  at: number
}

export function trackState(status: RunStatus, hasStarted: boolean): TrackState {
  const fill = (at: number, current: SegmentState): TrackState => ({
    at,
    segments: STAGES.map((_, i) =>
      i < at ? 'done' : i === at ? current : 'todo'
    ),
  })
  switch (status) {
    case 'queued':
      return fill(0, 'current')
    case 'provisioning':
      return fill(1, 'current')
    case 'running':
      return fill(2, 'current')
    case 'awaiting_approval':
      return fill(2, 'waiting')
    case 'paused':
      return fill(2, 'paused')
    case 'verifying':
      return fill(3, 'current')
    case 'completed':
      return { at: 4, segments: STAGES.map(() => 'done') }
    case 'cancelled':
    case 'handed_off':
      // Handed off: the work continues in a child run; this one just stopped.
      return fill(hasStarted ? 2 : 1, 'stopped')
    default:
      // failed / blocked / timed_out
      return fill(hasStarted ? 2 : 1, 'failed')
  }
}

const SEGMENT_CLASS: Record<SegmentState, string> = {
  done: 'bg-moss',
  current:
    'bg-info motion-safe:animate-[track-pulse_1.6s_ease-in-out_infinite]',
  waiting: 'bg-signal',
  paused: 'bg-signal',
  failed: 'bg-danger',
  stopped: 'bg-ink-3',
  todo: 'bg-line',
}

/** Compact track for list rows. */
export function RunTrackMini({
  status,
  hasStarted,
}: {
  status: RunStatus
  hasStarted: boolean
}) {
  const { segments } = trackState(status, hasStarted)
  return (
    <span className="inline-flex gap-[3px]" aria-hidden>
      {segments.map((segment, i) => (
        <span
          key={i}
          className={`h-1.5 w-3.5 rounded-full ${SEGMENT_CLASS[segment]}`}
        />
      ))}
    </span>
  )
}

/** Full track for the run detail header, with stage names and timestamps. */
export function RunTrack({
  status,
  hasStarted,
  captions = {},
}: {
  status: RunStatus
  hasStarted: boolean
  /** Optional caption under a stage, keyed by stage index (e.g. start time). */
  captions?: Partial<Record<number, string>>
}) {
  const { segments, at } = trackState(status, hasStarted)
  return (
    <ol className="grid grid-cols-5 gap-1.5" aria-label="Run progress">
      {STAGES.map((stage, i) => {
        const segment = segments[i]
        const isAt = i === at && segment !== 'done'
        const label =
          isAt && segment === 'waiting'
            ? 'Waiting for you'
            : isAt && segment === 'paused'
              ? 'Paused'
              : isAt && segment === 'failed'
                ? `Stopped at ${stage.toLowerCase()}`
                : isAt && segment === 'stopped'
                  ? status === 'handed_off'
                    ? 'Handed off'
                    : 'Cancelled'
                  : stage
        return (
          <li
            key={stage}
            className="min-w-0"
            aria-current={isAt ? 'step' : undefined}
          >
            <span
              className={`block h-2 rounded-full ${SEGMENT_CLASS[segment]}`}
            />
            <span
              className={`mt-2 block truncate text-sm ${
                isAt
                  ? segment === 'waiting' || segment === 'paused'
                    ? 'font-semibold text-signal'
                    : segment === 'failed'
                      ? 'font-semibold text-danger'
                      : 'font-semibold text-ink'
                  : segment === 'done'
                    ? 'text-ink-2'
                    : 'text-ink-3'
              }`}
            >
              {label}
            </span>
            {captions[i] ? (
              <span className="block truncate text-xs text-ink-3">
                {captions[i]}
              </span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
